import { CtBinaryOperator, CtControlTypes, CtModelOperator, CtSelectControlValue } from "@ctsolution/ct-framework";
import { CustomCompanySetupEnum } from "../../components/company/company-edit/company-edit.component";

/**
 * Configurazione di un singolo campo LeadInformation da aggiungere dinamicamente
 * al form del lead.
 *
 * - `type`           : codice numerico dell'enum ContactInformationTypeVO (es. 101)
 * - `controlType`    : tipo di controllo Angular Material (default: CtControlTypes.TEXT)
 * - `flexPercentage` : larghezza percentuale nella griglia del form (default: 50)
 */
export interface LeadInformationFieldConfig {
  type: number;
  controlType?: CtControlTypes;
  flexPercentage?: number;
}

/**
 * Filtro costante da applicare a una lista, dichiarato per-client nel MenuJson.
 *
 * - `field`        : campo su cui filtrare, nella forma usata dalle liste (es. "ContactsCode")
 * - `value`        : valore del filtro
 * - `operatorType` : codice numerico di CtBinaryOperator (default: 0 = Equal)
 */
export interface ConstantFilterConfig {
  field: string;
  value: any;
  operatorType?: CtBinaryOperator;
}

/**
 * Campo valorizzato automaticamente per-client dal MenuJson.
 *
 * - `field` : nome del control nel form (lo stesso passato a `setName`)
 * - `value` : valore da impostare, **nella forma che il control si aspetta**.
 *             Per un input di testo una stringa; per un lookup o una select l'oggetto
 *             completo dell'opzione (es. `{ "label": "...", "value": 123 }`).
 */
export interface AutoFillFieldConfig {
  field: string;
  value: any;
}

/**
 * Riferimento Cliente↔Contatto creato automaticamente al salvataggio del cliente,
 * dichiarato per-client nel MenuJson.
 *
 * - `contactCode` : codice del contatto da associare (campo `Code` di CTContact). Si usa il
 *                   codice e non l'Oid perche' l'Oid cambia da un ambiente all'altro, mentre
 *                   il codice e' lo stesso su test, demo e produzione.
 * - `type`        : codice del tipo di riferimento (enum ContactCompanyType, es. 5 = Defined)
 */
export interface DefaultContactReferenceConfig {
  contactCode: string;
  type: number;
}

/**
 * Azione massiva sulle righe selezionate di una lista, dichiarata per-client nel MenuJson.
 *
 * - `action`           : action del backend chiamata in POST sul controller della lista (es. "SetActive")
 * - `payload`          : campi aggiunti al body accanto a `Oids` e `Filters` (es. `{ "Active": false }`)
 * - `label`, `icon`    : pulsante in toolbar (chiave i18n e icona Material)
 * - `confirmTitle`, `confirmContent`, `doneMessage` : chiavi i18n della conferma e dell'esito;
 *                        `confirmContent` e `doneMessage` ricevono `{{count}}`
 * - `selectableWhen`   : solo le righe con `field` uguale a `value` hanno la checkbox
 *                        (es. `{ "field": "Active", "value": true }`: i gia' disabilitati restano fuori)
 * - `selectAllEnabled` : offre il toggle "tutti i risultati del filtro" (default false: va chiesto)
 *
 * Le chiavi i18n non dichiarate restano quelle di default del framework (`CT_PAGE.CT_DATATABLE.bulk*`).
 */
export interface BulkActionConfig {
  action: string;
  payload?: { [key: string]: any };
  label?: string;
  icon?: string;
  confirmTitle?: string;
  confirmContent?: string;
  doneMessage?: string;
  selectableWhen?: { field: string; value: any };
  selectAllEnabled?: boolean;
}

export class CTConfigurationModel {

  visible: boolean = true;
  customCompanySetup: CustomCompanySetupEnum = CustomCompanySetupEnum.BASE;
  customContactSetup: CustomCompanySetupEnum = CustomCompanySetupEnum.BASE;
  customCompanyTemplate = 'company-information-model.json';
  customContactTemplate = 'contact-information-model.json';
  label?: string;
  type?: number;
  cols: Array<string> = [];
  visibleWorkOrder: boolean = true;
  doubleClickOpenDetail: boolean = false;
  expanded: boolean = false;
  formatPrice: number = 2;
  formatNumber: number = 2;
  multiCheckBoxTable: boolean = false;
  bypassLeadStatus: boolean = false;
  useDefaultVAT: boolean = false;
  quotationVisible: boolean = false;

  /**
   * Quando `true`, il default del lookup "Tipologia cliente" (CompanyType) viene preso
   * dall'elemento del datasource marcato come `Default = true` a DB (vedi tabella CompanyType,
   * colonna `Default`).
   *
   * Default `false` per backward-compatibility: gli altri client continuano a usare il
   * default basato sul macro-tipo (Private/Business) impostato via `setType`.
   *
   * Va abilitato esplicitamente per-client tramite
   * `getComponentOverride("company", "companyType", configurationModel)`.
   */
  useDefaultCompanyType: boolean = false;

  visibleHintRowWorkOrder: boolean = true;
  cloneRowEnabled: boolean = true;
  
  // Abilita la modalità ConfigurationCode per le Quotation (passa ConfigurationCode nel body invece di filtri client-side)
  enableConfigurationCodeMode: boolean = false;
  exportExcelEnabled: boolean = false;

  /**
   * Abilita la card "Reportistica" in dashboard.
   * Alla pressione chiama GET BI/GetAccess e apre il link SSO restituito in una nuova scheda.
   * Default `false` per backward-compatibility: va abilitata esplicitamente per-client
   * tramite `getComponentOverride("dashboard", "biConfiguration", ...)` nel portale.
   */
  biConfiguration: boolean = false;

  // Controlla se abilitare la navigazione onCreate nel work-order-list
  allowOnCreate: boolean = true;

  // Controlla se mostrare il bottone Detail nella handling-list
  // E se mostrare la textarea ItemNotes nello step1 del wizard (default true = backward compatible)
  handlingDetailEnabled: boolean = true;

  /**
   * Abilita la sezione LeadInformation nel form del lead.
   * Default `false` per backward-compatibility: la funzionalità si attiva
   * esplicitamente per-client tramite `getComponentOverride`.
   */
  leadInformationEnabled: boolean = false;

  /**
   * Elenco dei campi LeadInformation da aggiungere dinamicamente al form del lead.
   * Ogni entry specifica il codice del tipo, il tipo di controllo e la larghezza.
   * Valori omessi vengono completati con i default (TEXT, 50%).
   * Esempio:
   *   [
   *     { type: 101, controlType: CtControlTypes.TEXT, flexPercentage: 50 },
   *     { type: 102, controlType: CtControlTypes.TEXTAREA, flexPercentage: 100 }
   *   ]
   */
  leadInformationFields: LeadInformationFieldConfig[] = [];

  // Colonne da mostrare nella dialog di dettaglio articoli dell'ordine.
  // Valori ammessi: 'code' | 'name' | 'description' | 'price' | 'qty' | 'additionalCode' | 'price1000'
  handlingDetailVisibleColumns: string[] = ['code', 'name', 'description', 'additionalCode', 'price', 'price1000', 'qty'];

  showTotalRow: boolean = false;
  legendsItem: Array<any> = [
    { color: '#A82A26', label: 'Presentation Call', description: 'Appuntamento di presentazione ufficiale con cliente o prospect' },
    { color: '#2196F3', label: 'Appuntamento', description: 'Appuntamento con potenziale cliente' },
    { color: '#E1C11F', label: 'Mail', description: 'Mail in attesa di essere inviata' },
    { color: '#06820D', label: 'Inizio lavori', description: 'Avvio operativo' },
    { color: '#780E82', label: 'Offerta - Da inviare', description: 'Offerta pronta, in attesa di essere inviata' },
    { color: '#FF9800', label: 'Call', description: 'Telefonata per seguire una trattativa già avviata' },
    { color: '#FF148D', label: 'Lead - Attività proattiva', description: 'Iniziativa autonoma verso nuovi lead' },
    { color: '#12CED5', label: 'Offerta - In trattativa', description: 'Offerta inviata, in attesa di risposta o trattativa attiva' },
    { color: '#884916', label: 'Verifica stato', description: 'Controllo avanzamento lavori' },
    { color: '#44D130', label: 'Proattiva', description: 'Promemoria di proattività' },
    { color: '#008080', label: 'Follow up', description: 'Controllo stato avanzamento' },
    { color: '#000000', label: 'Kick Off', description: 'Kick off con cliente o interno' }
  ];

  visibleFields: string[] = [
    'DisplayName',
    'ReferenceName',
    'FiscalCode',
    'VAT',
    'Places'
  ];

  valueOptions: any | null = null;

  /**
   * Override delle operazioni abilitate sulla datatable del modulo payments.
   *
   * - `null` (default): mantiene il comportamento legacy hard-coded in `payments-list`
   *   (Update, Detail, Export). Nessun impatto sui client esistenti.
   * - valorizzato: sostituisce completamente l'elenco delle operazioni con quanto
   *   fornito (es. `[Update, Detail, Export, Create]` per abilitare la creazione).
   *
   * Va impostato esplicitamente per-client tramite
   * `getComponentOverride("payments", "paymentConfiguration", ...)`.
   */
  paymentsOperations: CtModelOperator[] | null = null;

  /**
   * Override dei campi mostrati nel form di edit/create del modulo payments.
   *
   * - `null` (default): il form mostra tutti i controlli storici
   *   (Description, Amount, PaymentDate, Status, Company, UserInfo, Contact, Notes).
   *   Nessun impatto sui client esistenti.
   * - valorizzato: filtra (e riordina) i controlli mantenendo solo quelli i cui
   *   `name` sono elencati nell'array, nell'ordine fornito. I controlli non
   *   inclusi vengono rimossi dal form.
   *
   * I nomi accettati corrispondono ai `name` dei `CtControlConfiguration` definiti
   * in `PaymentsEditComponent`: "Description", "Amount", "PaymentDate",
   * "ExpireDate", "Status", "Company", "WorkOrder", "UserInfo", "Contact", "Notes".
   *
   * Va impostato esplicitamente per-client tramite
   * `getComponentOverride("payments", "paymentConfiguration", ...)`.
   */
  paymentsFormControls: string[] | null = null;

  /**
   * Override dei campi che devono essere `required` nel form di edit/create del modulo payments.
   *
   * - `null` (default): nessun campo viene reso required oltre a quanto già dichiarato
   *   nei `CtControlConfiguration` originali. Nessun impatto sui client esistenti.
   * - valorizzato: i control il cui `name` è elencato vengono forzati a `required = true`
   *   tramite `Validators.required` runtime, applicato dopo l'eventuale filtro di
   *   `paymentsFormControls`.
   *
   * I nomi accettati sono gli stessi di `paymentsFormControls`: "Description",
   * "Amount", "PaymentDate", "ExpireDate", "Status", "Company", "WorkOrder",
   * "UserInfo", "Contact", "Notes".
   *
   * Va impostato esplicitamente per-client tramite
   * `getComponentOverride("payments", "paymentConfiguration", ...)`.
   */
  paymentsRequiredFormControls: string[] | null = null;

  /**
   * Override del filtro `IsSelf` sul lookup Company nel form payments.
   *
   * - `null` (default): applica `IsSelf = true` (comportamento storico: il lookup
   *   mostra solo le aziende marcate come "azienda propria" del tenant). Nessun
   *   impatto sui client esistenti.
   * - `true`: applica `IsSelf = true` (equivalente al default, esplicito).
   * - `false`: NON applica il filtro su `IsSelf` (il lookup mostra tutte le aziende:
   *   utile quando il pagamento deve essere associato a un cliente).
   *
   * Va impostato esplicitamente per-client tramite
   * `getComponentOverride("payments", "paymentConfiguration", ...)`.
   */
  paymentCompanyLookupSelfOnly: boolean | null = null;

  /**
   * Visibilità della sezione "Dati fatturazione" nel form del Task edit.
   *
   * - `false` (default): la sezione (titolo + <ct-form>) NON viene renderizzata.
   *   `invoiceConfiguration` resta `null` e i campi InvoiceNumber/InvoiceDate/
   *   InvoiceState/InvoiceTotal/InvoiceVAT/InvoiceComments non compaiono.
   *   Il submit ignora la sezione e non manda al BE i relativi dati (i valori
   *   a DB restano invariati: fix solo FE).
   * - `true`: sezione visibile (comportamento storico). Va abilitato esplicitamente
   *   per-client tramite `getComponentOverride("task", "taskConfiguration", ...)`.
   */
  taskInvoiceSectionVisible: boolean = false;

  /**
   * Filtri costanti da applicare alla lista, dichiarati per-client nel MenuJson.
   *
   * - `null` (default): nessun filtro aggiuntivo, la lista resta quella storica.
   *   Nessun impatto sui client esistenti.
   * - valorizzato: ogni entry diventa un filtro costante, quindi non rimovibile
   *   dall'utente dai filtri di colonna. Se il campo e' gia' filtrato, il valore
   *   dichiarato qui sostituisce quello esistente (upsert).
   *
   * Va impostato esplicitamente per-client tramite
   * `getComponentOverride("<componente>", "<configurazione della lista>", ...)`.
   */
  constantFilters: ConstantFilterConfig[] | null = null;

  /**
   * Override delle operazioni abilitate sulla datatable della lista.
   *
   * - `null` (default): la lista mantiene le operazioni decise dal componente.
   *   Nessun impatto sui client esistenti.
   * - valorizzato: sostituisce completamente l'elenco delle operazioni (es. `[Create]`
   *   per lasciare la creazione e togliere l'eliminazione).
   *
   * L'elenco viene riapplicato dopo l'inizializzazione della configurazione: il merge
   * con il config del backend reintroduce le Operations del server e sovrascriverebbe
   * quanto impostato qui.
   *
   * Va impostato esplicitamente per-client tramite
   * `getComponentOverride("<componente>", "<configurazione della lista>", ...)`.
   */
  datatableOperations: CtModelOperator[] | null = null;

  /**
   * Campi del form valorizzati automaticamente, dichiarati per-client nel MenuJson.
   *
   * - `null` (default): nessuna valorizzazione, il form resta quello storico.
   *   Nessun impatto sui client esistenti.
   * - valorizzato: ogni entry imposta il valore del control indicato, sia in creazione
   *   sia in modifica. In modifica il valore dichiarato **sovrascrive** quello caricato
   *   dal record: e' voluto, perche' il campo e' forzato per quel ruolo.
   *
   * Va impostato esplicitamente per-client tramite `getComponentOverride`.
   */
  autoFillFields: AutoFillFieldConfig[] | null = null;

  /**
   * Campi del form che l'utente non deve poter modificare, dichiarati per-client nel MenuJson.
   *
   * - `null` (default): nessun campo bloccato. Nessun impatto sui client esistenti.
   * - valorizzato: i control elencati vengono disabilitati. Si usa `setDisabled` e non il
   *   solo `readonly` perche' `readonly` vale sugli input di testo ma non su lookup e select,
   *   dove il campo resterebbe modificabile.
   *
   * **Il form che li usa deve leggere i valori con `getRawValue()`**: con `form.value` i
   * control disabilitati non finiscono nel payload e il valore andrebbe perso.
   *
   * Va impostato esplicitamente per-client tramite `getComponentOverride`.
   */
  readonlyFields: string[] | null = null;

  /**
   * Campi del form che l'utente non deve vedere, dichiarati per-client nel MenuJson.
   *
   * - `null` (default): nessun campo nascosto. Nessun impatto sui client esistenti.
   * - valorizzato: i control elencati vengono nascosti con `setHidden`. Il control resta nel
   *   form, quindi il valore gia' presente sul record viene riletto e salvato com'e'.
   *
   * Non nascondere campi obbligatori: l'utente non potrebbe compilarli e il salvataggio
   * resterebbe bloccato senza un errore visibile.
   *
   * Va impostato esplicitamente per-client tramite `getComponentOverride`.
   */
  hiddenFields: string[] | null = null;

  /**
   * Disponibilita' del download PDF nella lista contratti (voci del menu azioni e bottone
   * nella colonna azioni).
   *
   * - `true` (default): comportamento storico, il download resta disponibile.
   *   Nessun impatto sui client esistenti.
   * - `false`: nessuna voce di download. Serve ai ruoli a cui il PDF non va dato.
   *
   * Va impostato esplicitamente per-client tramite `getComponentOverride`.
   */
  downloadPdfEnabled: boolean = true;

  /**
   * Disponibilita' del download dello ZIP dei documenti nella sezione contratti del dettaglio cliente
   * (icona nella colonna azioni).
   *
   * - `true` (default): comportamento storico, l'icona resta. Nessun impatto sui client esistenti.
   * - `false`: nessuna icona. Serve ai ruoli a cui i documenti del contratto non vanno dati.
   *
   * Va impostato esplicitamente per-client tramite `getComponentOverride`.
   */
  downloadZipEnabled: boolean = true;

  /**
   * Riferimento da associare sempre al cliente, dichiarato per-client nel MenuJson.
   *
   * - `null` (default): nessun riferimento automatico. Nessun impatto sui client esistenti.
   * - valorizzato: al salvataggio del cliente, se il riferimento non c'e' gia', viene creato.
   *   Vale sia in creazione sia in modifica, cosi' il riferimento e' sempre presente.
   *   Il contatto viene risolto a runtime dal suo codice, quindi la stessa configurazione
   *   funziona su tutti gli ambienti.
   *
   * Va impostato esplicitamente per-client tramite `getComponentOverride`.
   */
  defaultContactReference: DefaultContactReferenceConfig | null = null;

  /**
   * Click dei contatori dei contratti in dashboard verso la lista corrispondente.
   *
   * - `true` (default): comportamento storico, il contatore apre la lista.
   *   Nessun impatto sui client esistenti.
   * - `false`: il contatore mostra solo il numero. Serve ai ruoli che non devono
   *   raggiungere la lista contratti.
   *
   * Va impostato esplicitamente per-client tramite
   * `getComponentOverride("dashboard", "workOrderCounterConfiguration", ...)`.
   */
  counterNavigationEnabled: boolean = true;

  /**
   * Indirizzo esterno aperto dall'azione di modifica di una lista, al posto del dettaglio.
   *
   * - `null` (default): comportamento storico, l'azione apre il dettaglio del record.
   *   Nessun impatto sui client esistenti.
   * - valorizzato: l'azione apre l'indirizzo in una nuova scheda. Serve ai ruoli che non
   *   gestiscono il record nell'applicazione ma su un sito esterno.
   *
   * Va impostato esplicitamente per-client tramite `getComponentOverride`.
   */
  editExternalUrl: string | null = null;

  /**
   * Colonne da mostrare in una lista, dichiarate per-client nel MenuJson con il nome del campo
   * (il `Field` di `CTFieldInfos`, es. "StartAt").
   *
   * - `null` (default): comportamento storico, la lista mostra le colonne decise dal backend.
   *   Nessun impatto sui client esistenti.
   * - valorizzato: le colonne non elencate vengono nascoste. E' una lista di quelle da tenere e
   *   non di quelle da togliere, cosi' una colonna aggiunta domani dal backend non compare da sola
   *   nei ruoli che devono vederne poche. L'ordine resta quello della lista.
   *
   * Va impostato esplicitamente per-client tramite `getComponentOverride`.
   */
  visibleColumns: string[] | null = null;

  /**
   * Tipo indirizzo preselezionato quando si aggiunge una sede al cliente, dichiarato per-client nel
   * MenuJson come campo e valore che individuano il PlaceType, es. `{ "field": "Description", "value": "Sede Legale" }`.
   *
   * - `null` (default): nessuna preselezione, la sede nuova nasce vuota come oggi.
   *   Nessun impatto sui client esistenti.
   * - valorizzato: il PlaceType viene cercato a runtime nel lookup confrontando i valori come testo
   *   (`operatorType` non viene usato). Si dichiara un campo e non l'Oid perche' l'Oid cambia da un
   *   ambiente all'altro. Se non viene trovato, la sede nasce vuota.
   *
   * Va impostato esplicitamente per-client tramite `getComponentOverride`.
   */
  defaultPlaceType: ConstantFilterConfig | null = null;

  /**
   * Azione massiva sulle righe selezionate della lista, dichiarata per-client nel MenuJson.
   *
   * - `null` (default): nessuna selezione a checkbox e nessun pulsante. Nessun impatto sui
   *   client esistenti.
   * - valorizzato: la lista mostra le checkbox e il pulsante in toolbar; al click conferma con
   *   il conteggio e chiama l'action del backend con gli Oid selezionati o con i filtri correnti
   *   ("tutti i risultati del filtro"). Vedi `BulkActionConfig`.
   *
   * Va impostato esplicitamente per-client tramite `getComponentOverride`.
   */
  bulkAction: BulkActionConfig | null = null;

  constructor() { }

  public static create = (): CTConfigurationModel => new CTConfigurationModel();

  setVisible(value: boolean): CTConfigurationModel {
    this.visible = value;
    return this;
  }

  setLabel(value: string): CTConfigurationModel {

    this.label = value;
    return this;

  }

  setType(value: number): CTConfigurationModel {
    this.type = value;
    return this;
  }

  setCols(value: Array<string>): CTConfigurationModel {
    this.cols = value;
    return this;
  }

  setCustomSetup(value: CustomCompanySetupEnum): CTConfigurationModel {
    this.customCompanySetup = value;
    return this;
  }
  setDoubleClickOpenDetail(value: boolean): CTConfigurationModel {
    this.doubleClickOpenDetail = value;
    return this;
  }

  setExpanded(value: boolean): CTConfigurationModel {

    this.expanded = value;
    return this;

  }

  setLegendsItem(value: Array<any>): CTConfigurationModel {

    this.legendsItem = value;
    return this;

  }

  setBiConfiguration(value: boolean): CTConfigurationModel {

    this.biConfiguration = value;
    return this;

  }

  setUseDefaultCompanyType(value: boolean): CTConfigurationModel {

    this.useDefaultCompanyType = value;
    return this;

  }

  setPaymentsOperations(value: CtModelOperator[] | null): CTConfigurationModel {

    this.paymentsOperations = value;
    return this;

  }

  setPaymentsFormControls(value: string[] | null): CTConfigurationModel {

    this.paymentsFormControls = value;
    return this;

  }

  setPaymentsRequiredFormControls(value: string[] | null): CTConfigurationModel {

    this.paymentsRequiredFormControls = value;
    return this;

  }

  setPaymentCompanyLookupSelfOnly(value: boolean | null): CTConfigurationModel {

    this.paymentCompanyLookupSelfOnly = value;
    return this;

  }

  setTaskInvoiceSectionVisible(value: boolean): CTConfigurationModel {

    this.taskInvoiceSectionVisible = value;
    return this;

  }

  setConstantFilters(value: ConstantFilterConfig[] | null): CTConfigurationModel {

    this.constantFilters = value;
    return this;

  }

  setDatatableOperations(value: CtModelOperator[] | null): CTConfigurationModel {

    this.datatableOperations = value;
    return this;

  }

  setAutoFillFields(value: AutoFillFieldConfig[] | null): CTConfigurationModel {

    this.autoFillFields = value;
    return this;

  }

  setReadonlyFields(value: string[] | null): CTConfigurationModel {

    this.readonlyFields = value;
    return this;

  }

  setDownloadPdfEnabled(value: boolean): CTConfigurationModel {

    this.downloadPdfEnabled = value;
    return this;

  }

  setDownloadZipEnabled(value: boolean): CTConfigurationModel {

    this.downloadZipEnabled = value;
    return this;

  }

  setDefaultContactReference(value: DefaultContactReferenceConfig | null): CTConfigurationModel {

    this.defaultContactReference = value;
    return this;

  }

  setCounterNavigationEnabled(value: boolean): CTConfigurationModel {

    this.counterNavigationEnabled = value;
    return this;

  }

  setVisibleColumns(value: string[] | null): CTConfigurationModel {

    this.visibleColumns = value;
    return this;

  }

  setDefaultPlaceType(value: ConstantFilterConfig | null): CTConfigurationModel {

    this.defaultPlaceType = value;
    return this;

  }

  setBulkAction(value: BulkActionConfig | null): CTConfigurationModel {

    this.bulkAction = value;
    return this;

  }

}
